import type * as THREE from "three";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";

// `linewidth` is in CSS pixels (see createOutlineLine). Dash and gap are in scene metres divided by
// `dashScale`, which ParcelShape locks to the map zoom so the pattern keeps a constant on-screen length.
const OUTLINE_LINE_WIDTH = 4;
const OUTLINE_DASH_SIZE = 4;
const OUTLINE_GAP_SIZE = 2.5;
const OUTLINE_DASH_PATTERN_LENGTH = OUTLINE_DASH_SIZE + OUTLINE_GAP_SIZE;

// Pattern units per second, and the zoom at which one pattern unit is one scene metre.
const OUTLINE_DASH_SPEED = 4;
const OUTLINE_DASH_REFERENCE_ZOOM = 17;

function createOutlineMaterial({ color, isDashed }: { color: THREE.ColorRepresentation; isDashed: boolean }): LineMaterial {
	return new LineMaterial({
		color,
		linewidth: OUTLINE_LINE_WIDTH,
		dashed: isDashed,
		dashSize: OUTLINE_DASH_SIZE,
		gapSize: OUTLINE_GAP_SIZE,
		// The outline is coplanar with the parcel and zone fills at y=0, and sits under the depth MapLibre's
		// building extrusions write into the shared framebuffer. Skipping depth entirely (as the vertex
		// handles in workspace-3d do) removes every z-fight and keeps the boundary visible; `transparent`
		// puts it in the late render pass so `renderOrder` can place it above the fills.
		transparent: true,
		depthTest: false,
		depthWrite: false,
	});
}

function createOutlineLine({ points, material }: { points: Array<THREE.Vector3>; material: LineMaterial }): Line2 {
	const positions: Array<number> = [];

	for (const point of points) {
		positions.push(point.x, point.y, point.z);
	}

	// Rings arrive open (ParcelShapeService strips the repeated last vertex) and LineGeometry — unlike
	// Polyline, which re-closes loops while syncing — does not close them. Repeat the first vertex.
	positions.push(points[0].x, points[0].y, points[0].z);

	const geometry = new LineGeometry();
	geometry.setPositions(positions);

	const line = new Line2(geometry, material);
	// Feeds the per-segment distance attributes; without it the dash shader has nothing to march along.
	line.computeLineDistances();
	// Above the coplanar fills, which sit at the default render order.
	line.renderOrder = 1;
	// Matches the rest of the scene: the camera matrices are rebuilt from MapLibre every frame.
	line.frustumCulled = false;
	// Line2 defaults `resolution` to the renderer viewport, which MapLibreRendererService sets in DEVICE
	// pixels — that would silently redefine `linewidth` as device pixels (4px reads as 2 CSS px on a 2x
	// display). Feed it the canvas CSS size instead, matching three's documented units and MapLibre's own
	// `line-width`. It has to happen here: onBeforeRender runs immediately before the draw call, so a value
	// written earlier in the frame would be overwritten.
	line.onBeforeRender = (renderer) => {
		material.resolution.set(renderer.domElement.clientWidth, renderer.domElement.clientHeight);
	};

	return line;
}

export { createOutlineLine, createOutlineMaterial, OUTLINE_DASH_PATTERN_LENGTH, OUTLINE_DASH_REFERENCE_ZOOM, OUTLINE_DASH_SPEED };
